import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from '@pharvio/accounts/admin-account/data-access';
import {
  LoadProducts,
  ProductsState,
  SearchProducts
} from '@pharvio/catalog/data-access';
import {
  FulfillmentMode,
  ICreateOrderLinePayload,
  ICreateOrderPayload,
  inputNotNil,
  ILocalProductItem,
  StorefrontUrlService,
  OfflineModeService,
  OfflineOrderFulfillment
} from '@pharvio/core/shared';
import {
  AlertModule,
  AlertService,
  AmountToMajorPipe,
  LayoutToolbarComponent,
  PricePipe,
  TrackService
} from '@pharvio/core/ui';
import {
  Customer,
  CustomersState,
  LoadCustomers,
  SearchCustomers
} from '@pharvio/customers/data-access';
import {
  DraftOrder,
  GeneratePayByLink,
  OrdersState
} from '@pharvio/orders/data-access';
import { PosDialogService } from '@pharvio/pos/ui';
import {
  OfflineDialogService,
  OfflineOrderQrService,
  OfflineSigningKeyMissingError,
  OutboxService
} from '@pharvio/offline';

import { from, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { OrderPaths } from '../../../../core/constants/routes/orders-routes';

interface OrderLineItem {
  id: string;
  variantId: string;
  variantName: string;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
}

@Component({
  selector: 'app-create-order',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    LayoutToolbarComponent,
    PricePipe,
    AmountToMajorPipe,
    AlertModule
  ],
  templateUrl: './create-order.component.html'
})
export class CreateOrderComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly alert = inject(AlertService);
  private readonly posDialog = inject(PosDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly storefrontUrlService = inject(StorefrontUrlService);
  private readonly offlineMode = inject(OfflineModeService);
  private readonly outbox = inject(OutboxService);
  private readonly offlineQr = inject(OfflineOrderQrService);
  private readonly offlineDialog = inject(OfflineDialogService);
  private readonly track = inject(TrackService);

  /** Offline: the order becomes a signed QR the customer redeems on their phone. */
  readonly isOffline = this.offlineMode.isOffline;
  readonly canWorkOffline = this.offlineMode.canWorkOffline;

  readonly searchCtrl = new FormControl('');
  readonly customerSearchCtrl = new FormControl('');
  readonly searchResults$: Observable<ILocalProductItem[]> = this.store.select(
    ProductsState.productSearchResults
  );
  readonly searchLoading$: Observable<boolean> = this.store.select(
    ProductsState.productSearchLoading
  );
  readonly customers$: Observable<Customer[]> = this.store.select(
    CustomersState.filteredCustomers
  );

  showSearchResults = false;
  showCustomerResults = false;
  lines: OrderLineItem[] = [];
  submitting = false;

  customerId: string | null = null;
  customerName: string | null = null;
  customerPhone: string | null = null;
  customerEmail: string | null = null;

  readonly orderForm = new FormGroup({
    fulfillmentMode: new FormControl<FulfillmentMode>('pickup'),
    notesInternal: new FormControl(''),
    notesToCustomer: new FormControl('')
  });

  readonly fulfillmentOptions: { value: FulfillmentMode; label: string }[] = [
    { value: 'pickup', label: 'Pickup' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'in_store', label: 'In-Store' }
  ];

  readonly lineColumns = ['name', 'qty', 'price', 'total', 'remove'];

  get subtotalMinor(): number {
    return this.lines.reduce((sum, l) => sum + l.lineTotalMinor, 0);
  }

  ngOnInit(): void {
    const branchId =
      this.store.selectSnapshot(AuthState.currentBranch) ?? undefined;
    this.store.dispatch([
      new LoadProducts({
        categoryId: undefined,
        isActive: true,
        page: 1,
        size: 20,
        branchIds: branchId ? [branchId] : undefined
      }),
      new LoadCustomers({ branchId })
    ]);

    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((q) => {
        this.showSearchResults = true;
        this.store.dispatch(
          new SearchProducts({
            search: q ?? '',
            branchIds: branchId ? [branchId] : []
          })
        );
      });

    this.customerSearchCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((q) => {
        this.showCustomerResults = true;
        this.store.dispatch(new SearchCustomers({ search: q ?? '' }));
      });
  }

  onSearchFocus(): void {
    this.showSearchResults = true;
  }

  onCustomerSearchFocus(): void {
    this.showCustomerResults = true;
  }

  selectProduct(variant: ILocalProductItem): void {
    const price = variant?.retailPriceMinor || 0;
    const variantId = variant?.id;

    const existing = this.lines.find((l) => l.variantId === variantId);
    if (existing) {
      existing.quantity += 1;
      existing.lineTotalMinor = existing.unitPriceMinor * existing.quantity;
    } else {
      this.lines = [
        ...this.lines,
        {
          id: crypto.randomUUID(),
          variantId,
          variantName: variant.displayName,
          quantity: 1,
          unitPriceMinor: price,
          lineTotalMinor: price
        }
      ];
    }

    this.searchCtrl.setValue('', { emitEvent: false });
    this.showSearchResults = false;
    this.alert.success({
      message: `${variant.displayName} added.`,
      verticalPosition: 'top',
      horizontalPosition: 'end',
      duration: 2000
    });
    this.cdr.markForCheck();
  }

  updateQty(line: OrderLineItem, qty: number): void {
    if (qty < 1) return;
    line.quantity = qty;
    line.lineTotalMinor = line.unitPriceMinor * qty;
    this.cdr.markForCheck();
  }

  removeLine(lineId: string): void {
    this.lines = this.lines.filter((l) => l.id !== lineId);
    this.cdr.markForCheck();
  }

  selectCustomer(customer: Customer): void {
    this.customerId = customer.id;
    this.customerName = `${customer.firstName} ${customer.lastName}`;
    this.customerPhone = customer.phone ?? null;
    this.customerEmail = customer.email ?? null;
    this.customerSearchCtrl.setValue('', { emitEvent: false });
    this.showCustomerResults = false;
    this.cdr.markForCheck();
  }

  detachCustomer(): void {
    this.customerId = null;
    this.customerName = null;
    this.customerPhone = null;
    this.customerEmail = null;
    this.cdr.markForCheck();
  }

  submit(): void {
    if (this.lines.length === 0) {
      this.alert.error('Add at least one product to the order.');
      return;
    }
    if (this.submitting) return;
    this.submitting = true;
    this.cdr.markForCheck();

    const raw = this.orderForm.getRawValue();
    const branchId =
      this.store.selectSnapshot(AuthState.currentBranch) ?? undefined;

    const orderLines: ICreateOrderLinePayload[] = this.lines.map((l) => ({
      variantId: l.variantId,
      quantity: l.quantity
    }));

    const payload: ICreateOrderPayload = {
      source: 'pharmacist_initiated',
      fulfillmentMode: raw.fulfillmentMode ?? 'pickup',
      branchId,
      customerId: this.customerId ?? undefined,
      checkoutName: this.customerName ?? undefined,
      notesInternal: inputNotNil(raw.notesInternal)
        ? raw.notesInternal
        : undefined,
      notesToCustomer: inputNotNil(raw.notesToCustomer)
        ? raw.notesToCustomer
        : undefined,
      lines: orderLines
    };

    if (this.isOffline()) {
      this.submitOffline(payload, branchId);
      return;
    }

    this.store
      .dispatch(new DraftOrder(payload))
      .pipe(
        switchMap(() => {
          const order = this.store.selectSnapshot(OrdersState.orders).data[0];
          return this.store.dispatch(
            new GeneratePayByLink(order.id, { ttlMinutes: 30 })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          const order = this.store.selectSnapshot(OrdersState.lastCreatedOrder);
          // console.log('Order created:', order);
          if (order?.paymentLinkToken) {
            const paymentLinkUrl =
              this.storefrontUrlService.createCheckoutByTokenUrl(
                order.paymentLinkToken
              );

            this.posDialog.openPayLink({
              paymentLinkUrl: paymentLinkUrl,
              paymentLinkToken: order.paymentLinkToken,
              reference: order.paymentReference,
              total: order.totalMinor,
              customerName: this.customerName
            });
          }
          this.alert.success('Order created and payment link generated.');
          this.router.navigate([OrderPaths.List]);
        },
        error: () => {
          this.alert.error("Couldn't create the order. Please try again.");
          this.submitting = false;
          this.cdr.markForCheck();
        }
      });
  }

  // ── Offline: signed QR instead of a pay link ────────────────────────────

  private toQrFulfillment(
    mode: FulfillmentMode | null | undefined
  ): OfflineOrderFulfillment {
    switch (mode) {
      case 'delivery':
      case 'external_delivery':
        return 'external_delivery';
      case 'in_store':
      case 'in_store_immediate':
        return 'in_store_immediate';
      default:
        return 'pickup';
    }
  }

  private submitOffline(
    payload: ICreateOrderPayload,
    branchId: string | undefined
  ): void {
    const fail = (message: string) => {
      this.alert.error(message);
      this.submitting = false;
      this.cdr.markForCheck();
    };
    if (!this.canWorkOffline()) {
      fail(
        'No products have been synced to this device yet — reconnect and sync products first.'
      );
      return;
    }
    if (!branchId || branchId === 'none') {
      fail('Select a branch before creating an offline order.');
      return;
    }
    const tenantId = this.store.selectSnapshot(AuthState.tenantId);
    const userId = this.store.selectSnapshot(AuthState.currentUser)?.id;
    if (!tenantId || !userId) {
      fail('Your session is incomplete. Sign in again to continue.');
      return;
    }
    const clientReference = this.outbox.newClientReference();
    const totalMinor = this.subtotalMinor;

    from(
      this.offlineQr.build({
        orderPayload: payload,
        source: 'pharmacist_initiated',
        fulfillmentMode: this.toQrFulfillment(payload.fulfillmentMode),
        clientReference,
        customer: {
          name: this.customerName,
          phone: this.customerPhone,
          email: this.customerEmail
        },
        notesToCustomer: payload.notesToCustomer
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (qr) => {
          void this.outbox.enqueueQrIssued({
            cart: {
              id: clientReference,
              customerId: this.customerId,
              customerName: this.customerName,
              items: this.lines.map((l) => ({
                id: l.id,
                variantId: l.variantId,
                variantName: l.variantName,
                barcode: '',
                quantity: l.quantity,
                unitPriceMinor: l.unitPriceMinor,
                lineDiscountMinor: 0,
                lineTotalMinor: l.lineTotalMinor,
                isControlled: false
              })),
              cartDiscountMinor: 0,
              subtotalMinor: totalMinor,
              taxMinor: 0,
              totalMinor,
              parked: false,
              parkLabel: null,
              openedAt: new Date().toISOString()
            },
            orderPayload: payload,
            clientReference,
            tenantId,
            branchId,
            userId,
            shiftId: null,
            qr: {
              url: qr.url,
              payloadBytes: qr.payloadBytes,
              compressed: qr.compressed
            }
          });
          this.offlineDialog.openQr({
            url: qr.url,
            clientReference,
            totalMinor,
            customerName: this.customerName,
            lineCount: qr.lineCount,
            payloadBytes: qr.payloadBytes
          });
          this.track.track({
            name: 'offline_qr_issued',
            props: {
              surface: 'create_order',
              lineCount: qr.lineCount,
              payloadBytes: qr.payloadBytes,
              compressed: qr.compressed
            }
          });
          this.alert.success(
            'QR order created — the customer pays on their phone.'
          );
          this.router.navigate([OrderPaths.List]);
        },
        error: (err: Error) => {
          fail(
            err instanceof OfflineSigningKeyMissingError
              ? err.message
              : (err?.message ?? "Couldn't create the QR order.")
          );
        }
      });
  }

  trackByLineId(_: number, line: OrderLineItem): string {
    return line.id;
  }
}
